import { LitElement, html, css } from 'lit';
import { get, post, put } from '../../lib/ajax';
import { resizeImageSrc } from '../../lib/utils';
import $ from 'quantdom';

export class SavedItems extends LitElement {
	static styles = [
		css`
			:host {
				display: block;
				overflow-y: auto;
			}

			* {
				box-sizing: border-box;
			}

			a {
				color: inherit;
				text-decoration: none;
				transition: opacity 150ms ease-in-out;
			}

			a:hover {
				opacity: 0.7;
			}

			.saved-item img {
				width: 100%;
				height: auto;
				max-width: 80px;
			}

			.saved-item img[data-orientation='landscape'] {
				max-width: 100px;
			}

			.empty-wishlist {
				display: flex;
				width: 100%;
				justify-content: center;
				align-items: center;
				font-size: 25px;
				margin: 10% 0;
			}
			.empty-wishlist h5 {
				font-size: 18px;
				margin: 0;
				color: #131313;
				font-weight: 400;
			}

			.empty-wishlist a {
				background: #2e2e2e;
				color: #ffffff;
				padding: 0 10px;
				width: 100%;
				max-width: 110px;
				text-transform: uppercase;
				display: flex;
				justify-content: center;
				align-items: center;
				letter-spacing: 1px;
				font-size: 14px;
				height: 46px;
			}

			button.remove-save {
				transition: opacity 150ms ease-in-out;
			}

			button.remove-save:hover {
				opacity: 0.65;
			}

			button.remove-save:active > svg {
				opacity: 0.3;
				transition: opacity 0.3s ease-out;
			}

			.w-full {
				width: 100%;
			}

			.underline {
				text-decoration: underline;
			}

			.relative {
				position: relative;
			}

			.flex {
				display: flex;
			}

			.flex-col {
				flex-direction: column;
			}

			.justify-center {
				justify-content: center;
			}

			.justify-between {
				justify-content: space-between;
			}

			.items-start {
				align-items: flex-start;
			}

			.items-center {
				align-items: center;
			}

			.items-end {
				align-items: flex-end;
			}

			.leading-none {
				line-height: 1;
			}

			.details {
				display: flex;
				flex-direction: column;
				gap: 12px;
				line-height: 1;
			}

			.gap-5 {
				gap: 1.25rem;
			}

			.gap-4 {
				gap: 1rem;
			}

			.gap-3 {
				gap: 0.75rem;
			}

			.gap-2 {
				gap: 0.5rem;
			}
			.gap-1\.5 {
				gap: 0.375rem;
			}

			.gap-1 {
				gap: 0.25rem;
			}

			.pb-1\.5 {
				padding-bottom: 0.375rem;
			}

			.p-3 {
				padding: 0.75rem;
			}

			.uppercase {
				text-transform: uppercase;
			}

			.font-bold {
				font-weight: 700;
			}

			.font-sans {
				font-family: 'benton-sans-compressed', sans-serif;
			}

			.font-serif {
				font-family: 'adobe-caslon-pro', serif;
			}

			.tracking {
				letter-spacing: 1px;
			}

			.text-lg {
				font-size: 18px;
			}

			.text-base {
				font-size: 16px;
			}

			.text-sm {
				font-size: 14px;
			}

			.text-xs {
				font-size: 12px;
			}

			.saved-item {
				display: flex;
				align-items: center;
			}

			.view-print-link {
				color: #131313;
				text-decoration: underline;
				text-underline-position: under;
				text-underline-offset: 0.125rem;
			}

			.saved-item-details {
				color: #131313;
				display: flex;
				flex-direction: column;
			}

			.saved-items .saved-item {
				border-bottom: 1px solid #ddd;
				padding-bottom: 12px;
			}

			.saved-items .saved-item:first-child {
				margin-top: 12px;
			}

			.saved-items .saved-item:last-child {
				border: none;
			}

			button.remove-save {
				display: flex;
				flex: 1 1 auto;
				justify-content: flex-end;
				border: none;
				background: transparent;
				cursor: pointer;
				appearance: none;
				width: fit-content;
				max-width: 40px;
				height: 40px;
				padding: 0;
				color: var(--color-black);
				-webkit-tap-highlight-color: transparent; /* for removing the highlight */
			}

			button.remove-save svg {
				width: 12px;
				height: 12px;
			}

			.img-wrapper {
				background: #f7f7f7;
				height: 125px;
				min-width: 100px;
				display: flex;
				align-items: center;
				justify-content: center;
			}

			.saved-items[loading='true'] .remove-save {
				pointer-events: none;
				cursor: not-allowed;
			}
		`,
	];

	static properties = {
		items: { type: Array },
		variants: { type: Array },
		loading: { type: String },
	};

	_pushKlayvio() {
		window._learnq = window?._learnq || [];
		const wishlist = [...this.items].map((item) => item.productId);
		const email = this._getEmail();

		if (email) {
			window?._learnq?.push([
				'identify',
				{
					$email: email,
					wishlist: wishlist,
				},
			]);
		} else {
			console.error(
				'Error: Customer Email is null. No email to push wishlist to Klayvio'
			);
		}
	}

	async _updateWishlist() {
		try {
			if (this.customerId) {
				const url = `https://cw-shopify-api.sean-8bb.workers.dev/api/customers/${this.customerId}`;
				if (this.wishlistId) {
					const res = await put(`${url}/wishlists/${this.wishlistId}`, {
						count: this.items.length,
						items: this.items,
					});
					this._pushKlayvio();
				} else {
					const { id } = await post(`${url}/wishlist`, {
						count: this.items.length,
						items: this.items,
					});
					this._setWishlistId(id);
					this._pushKlayvio();
				}
			}
		} catch (e) {
			console.log(e);
		}
	}

	async syncedSaved() {
		const url = ` https://cw-shopify-api.sean-8bb.workers.dev/api/customers/${this.customerId}/wishlist`;
		const { id, count, items } = await get(url);

		const savedItems = items ?? [];
		localStorage.setItem('_hos-saved-list', JSON.stringify(savedItems));
		this._setWishlistId(id);

		if (items?.length > 0) {
			window.MicroBus.emit('save-items:change', {
				items: savedItems,
				count: savedItems.length,
				init: true,
			});
			savedItems.forEach((item) => {
				$(`save-button[variant-id="${item.variantId}"]`).each((el) => {
					$(el).attr('checked', 'true');
				});
			});
		}

		if (window?.wishlistVariant) {
			const { variantId } = window?.wishlistVariant;
			window.MicroBus.emit(`saved-input-toggle:${variantId}`, {
				checked: true,
			});
		}
	}

	_setCustomerId(id) {
		this.customerId = id;
		window.customer = id;
		sessionStorage.setItem('_hos-customer-id', id);
	}

	_getCustomerId() {
		return window.customer ?? sessionStorage.getItem('_hos-customer-id');
	}

	_getEmail() {
		this.email = sessionStorage.getItem('_hos-customer-email') ?? null;
		return this.email;
	}

	_getWishlistId() {
		if (
			sessionStorage.getItem('_hos-wishlist-id') &&
			sessionStorage.getItem('_hos-wishlist-id') != 'undefined'
		) {
			return sessionStorage.getItem('_hos-wishlist-id');
		} else if (this.customerId) {
			get(
				`https://cw-shopify-api.sean-8bb.workers.dev/api/customers/${this.customerId}/wishlist`
			).then((data) => {
				if (data?.id) {
					this._setWishlistId(data.id);
					return data.id;
				} else {
					return null;
				}
			});
		}
	}

	_setWishlistId(id) {
		sessionStorage.setItem('_hos-wishlist-id', id);
	}

	_getItems() {
		return localStorage.getItem('_hos-saved-list')
			? JSON.parse(localStorage.getItem('_hos-saved-list'))
			: [];
	}

	constructor() {
		super();
		this.items = this._getItems();
		this.loading = 'false';
		if (!window?.customer) {
			sessionStorage.removeItem('_hos-customer-id');
			sessionStorage.removeItem('_hos-wishlist-id');
			sessionStorage.removeItem('_hos-customer-email');
			this.items = [];
		}
	}

	async connectedCallback() {
		super.connectedCallback();
		this.items = this._getItems();
		this.pendingRequests = new Map();
		this.getVariants();
		this.products = [];
		this.variants = [];
		this.customerId = this._getCustomerId();
		this.wishlistId = this._getWishlistId();
		this.email = this._getEmail();

		window.MicroBus.on('customer:logged-in', async ({ detail }) => {
			const { customer } = detail;
			if (customer?.id) {
				this._setCustomerId(customer?.id);
				if (this.items.length > 0) {
					await this._updateWishlist();
				}
				await this.syncedSaved();
			}
		});

		window.MicroBus.on('save-items:change', ({ detail }) => {
			const { items } = detail;
			this.items = items;
			this._getCustomerId();
			this.getVariants();
			if (!detail?.init) {
				this._updateWishlist();
			}
		});

		if (this.customerId && !this.items.length) {
			this.syncedSaved();
		}
	}

	getVariants() {
		this.loading = 'true';
		this.getProducts().then((res) => {
			this.products = res.filter((product) => product != null);
			let temp = [];
			this.items.forEach((item) => {
				if (item) {
					const product = this.products?.find((el) => item.productId === el.id);
					const variant = product?.variants?.find(
						(variant) => variant.id === item.variantId
					);

					if (product && variant) {
						this.pendingRequests.delete(product.handle);
						variant.vendor = product?.vendor;
						variant.product_title = product.title;
						variant.product_url = product.url;
						variant.url = `/products/${product.handle}?variant=${variant.id}`;
						variant.main_image = product.media[0];
						variant.product_id = product.id;
						variant.handle = product.handle;
						variant.checked = true;
						temp.push(variant);
					}
				}
			});
			this.loading = 'false';
			this.variants = temp;
		});
	}

	async getProducts() {
		var promises = [];

		for (let item of this.items) {
			const previousController = this.pendingRequests.get(item?.handle);
			if (previousController) previousController.abort();
			const controller = new AbortController();
			promises.push(get(`/products/${item?.handle}.js`));
			this.pendingRequests.set(item.handle, controller);
		}
		return Promise.all(promises);
	}

	renderVariants() {
		if (this.variants.length > 0) {
			return html`
				<div class="saved-items flex flex-col gap-3" loading="${this.loading}">
					${this.variants.map((variant, i) => {
						const img = variant?.featured_image || variant.main_image;
						return html`
							<div class="saved-item gap-3" data-index="${i}">
								<a class="img-wrapper" href="${variant.url}">
									<img
										src="${resizeImageSrc(img.src, 650)}"
										width="${img?.width}"
										height="${img?.height}"
										alt="${img?.alt}"
										data-orientation=${img?.width / img?.height > 1
											? 'landscape'
											: 'portrait'}
									/>
								</a>
								<div class="saved-item-details w-full flex-col">
									<div class="flex justify-between items-start gap-3">
										<div class="flex flex-col gap-3">
											<a
												href="${variant.url}"
												class="font-sans uppercase font-bold leading-none tracking text-base"
												>${variant.product_title}</a
											>
											<div class="font-serif text-sm leading-none">
												${variant.vendor}
											</div>
											<a
												href="${variant.url}"
												class="font-serif text-sm view-print-link"
												>View Print</a
											>
										</div>
										<button
											class="remove-save"
											aria-label="Remove ${variant.product_title} from wishlist"
											index="${i}"
											@click="${() =>
												this._removeItem(
													variant.id,
													variant.product_id,
													variant.handle,
													variant.product_title
												)}"
										>
											<svg
												width="16"
												height="16"
												viewBox="0 0 16 16"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													fill-rule="evenodd"
													clip-rule="evenodd"
													d="M2.1199 0.707581L1.41326 0L0 1.41516L0.706632 2.12274L6.5882 8.01221L0.729864 13.8784L0.0232311 14.586L1.4365 16.0012L2.14313 15.2936L8.00147 9.42737L13.8598 15.2936L14.5664 16.0012L15.9797 14.586L15.2731 13.8784L9.41473 8.01221L15.2963 2.12274L16.0029 1.41516L14.5897 0L13.883 0.707581L8.00147 6.59705L2.1199 0.707581Z"
													fill="currentColor"
												/>
											</svg>
										</button>
									</div>
								</div>
							</div>
						`;
					})}
				</div>
			`;
		} else {
			return html`
				<div class="empty-wishlist flex flex-col gap-4">
					<h5 class="font-serif">No products in your wishlist.</h5>
					<a class="font-sans" href="/collections/all-prints" class="underline"
						>Shop Now</a
					>
				</div>
			`;
		}
	}
	async _removeItem(variantId, productId, handle, productTitle) {
		this.items = this.items.filter((item) => item.variantId !== variantId);

		if (window?.gtag) {
			window.gtag('event', `Wishlist - Remove Saved Item`, {
				event_detail: productTitle,
				variantId: variantId,
				productId: productId,
			});
		}
		window.MicroBus.emit(`saved-input-toggle:${variantId}`, {
			checked: false,
			variantId: variantId,
			productId: productId,
			handle: handle,
			items: this.items,
			count: this.items.length,
		});
		window.MicroBus.emit('save-items:count-change', {
			count: this.items.length,
		});
		this._updateWishlist();
		this.getVariants();
		localStorage.setItem('_hos-saved-list', JSON.stringify(this.items));
	}

	render() {
		return html` ${this.renderVariants()}</div>`;
	}
}
if (!customElements.get('saved-items'))
	customElements.define('saved-items', SavedItems);
